"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  normalizeCurrency,
  SUPPORTED_CURRENCIES,
  type Currency,
} from "@lib/currency"
import { STORE_BASE_CURRENCY } from "@lib/config"
import { syncCountrySelection } from "@lib/storefront-api-client"
import {
  COUNTRY_COOKIE_KEY,
  COUNTRY_MANUAL_COOKIE_KEY,
  COUNTRY_OPTIONS,
  CURRENCY_COOKIE_KEY,
  DEFAULT_COUNTRY_CODE,
  emitCountrySelectionEvent,
  emitCurrencySelectionEvent,
  normalizeCurrencyCode,
  normalizeSelectableCountryCode,
  normalizeSupportedCountryCode,
  readClientCountryManualSelection,
  readClientPersistedCountry,
  readCookieValue,
  resolveClientCountrySelection,
  resolveClientCurrencySelection,
  resolveMappedCurrencyForCountry,
} from "@lib/storefront-selection"

const COOKIE_MAX_AGE_DAYS = 365
const syncedInitialSelectionKeys = new Set<string>()

const parseManualSelectionFlag = (value: string | null): boolean | null => {
  if (!value) return null
  const normalized = value.trim().toLowerCase()
  if (["1", "true", "yes", "on"].includes(normalized)) return true
  if (["0", "false", "no", "off"].includes(normalized)) return false
  return null
}

const toNormalizedCurrency = (
  value: unknown,
  fallbackCountryCode: string
): Currency => {
  const fallback = resolveMappedCurrencyForCountry(fallbackCountryCode)
  return normalizeCurrency(
    normalizeCurrencyCode(value) || fallback,
    STORE_BASE_CURRENCY
  )
}

type FooterLocaleSwitcherProps = {
  initialCountryCode?: string
  initialCurrencyCode?: string
}

export default function FooterLocaleSwitcher({
  initialCountryCode,
  initialCurrencyCode,
}: FooterLocaleSwitcherProps) {
  const resolvedInitialCountry =
    normalizeSupportedCountryCode(initialCountryCode) || DEFAULT_COUNTRY_CODE
  const resolvedInitialCurrency = toNormalizedCurrency(
    initialCurrencyCode,
    resolvedInitialCountry
  )

  const [selectedCountry, setSelectedCountry] = useState<string>(
    resolvedInitialCountry
  )
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>(
    resolvedInitialCurrency
  )

  const [open, setOpen] = useState(false)
  const [countryListOpen, setCountryListOpen] = useState(false)
  const [countryQuery, setCountryQuery] = useState("")
  const [pendingCountry, setPendingCountry] = useState<string>(
    resolvedInitialCountry
  )
  const [pendingCurrency, setPendingCurrency] = useState<Currency>(
    resolvedInitialCurrency
  )

  const countryListRef = useRef<HTMLDivElement | null>(null)
  const cookieMaxAgeSeconds = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60

  const countries = useMemo(() => COUNTRY_OPTIONS, [])
  const currencies = useMemo(() => SUPPORTED_CURRENCIES, [])

  const selectedCountryOption = useMemo(
    () =>
      countries.find((option) => option.code === selectedCountry) ||
      countries[0],
    [countries, selectedCountry]
  )

  const pendingCountryOption = useMemo(
    () =>
      countries.find((option) => option.code === pendingCountry) ||
      countries[0],
    [countries, pendingCountry]
  )

  const filteredCountries = useMemo(() => {
    const query = countryQuery.trim().toLowerCase()
    if (!query) return countries
    return countries.filter((option) => {
      const byName = option.name.toLowerCase().includes(query)
      const byCode = option.code.toLowerCase().includes(query)
      return byName || byCode
    })
  }, [countries, countryQuery])

  const persistClientSideSelection = useCallback(
    ({
      country,
      currency,
      manualSelection,
    }: {
      country: string
      currency: string
      manualSelection: boolean
    }) => {
      if (typeof document !== "undefined") {
        const secure = window.location.protocol === "https:" ? "; secure" : ""
        document.cookie = `${COUNTRY_COOKIE_KEY}=${encodeURIComponent(
          country
        )}; path=/; max-age=${cookieMaxAgeSeconds}; samesite=lax${secure}`
        document.cookie = `${CURRENCY_COOKIE_KEY}=${encodeURIComponent(
          currency
        )}; path=/; max-age=${cookieMaxAgeSeconds}; samesite=lax${secure}`
        document.cookie = `${COUNTRY_MANUAL_COOKIE_KEY}=${
          manualSelection ? "1" : "0"
        }; path=/; max-age=${cookieMaxAgeSeconds}; samesite=lax${secure}`
      }

      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(COUNTRY_COOKIE_KEY, country)
          window.localStorage.setItem(CURRENCY_COOKIE_KEY, currency)
          window.localStorage.setItem(
            COUNTRY_MANUAL_COOKIE_KEY,
            manualSelection ? "1" : "0"
          )
        } catch {
          // ignore
        }
      }
    },
    [cookieMaxAgeSeconds]
  )

  const syncServerSelection = useCallback(
    async ({
      countryCode,
      currency,
      manualSelection,
    }: {
      countryCode: string
      currency: string
      manualSelection: boolean
    }) => {
      return syncCountrySelection({
        country: countryCode,
        currency,
        cookieMaxAgeDays: COOKIE_MAX_AGE_DAYS,
        manualSelection,
      })
    },
    []
  )

  const applySelection = useCallback(
    ({
      country,
      currency,
      manualSelection,
    }: {
      country: string
      currency: string
      manualSelection: boolean
    }) => {
      setSelectedCountry(country)
      setSelectedCurrency(currency as Currency)
      persistClientSideSelection({
        country,
        currency,
        manualSelection,
      })
      emitCountrySelectionEvent({ country, currency })
      emitCurrencySelectionEvent(currency)
    },
    [persistClientSideSelection]
  )

  const persistSelection = useCallback(
    async ({
      country,
      currency,
      manualSelection,
    }: {
      country: string
      currency: string
      manualSelection: boolean
    }) => {
      const normalizedCountry =
        normalizeSelectableCountryCode(country) || DEFAULT_COUNTRY_CODE
      const normalizedCurrency = toNormalizedCurrency(
        currency,
        normalizedCountry
      )

      applySelection({
        country: normalizedCountry,
        currency: normalizedCurrency,
        manualSelection,
      })

      let syncedCountry = normalizedCountry
      let syncedCurrency = normalizedCurrency

      try {
        const data = await syncServerSelection({
          countryCode: normalizedCountry,
          currency: normalizedCurrency,
          manualSelection,
        })

        syncedCountry =
          normalizeSupportedCountryCode(data?.country) || syncedCountry
        syncedCurrency = toNormalizedCurrency(data?.currency, syncedCountry)
      } catch {
        // ignore
      }

      if (
        syncedCountry !== normalizedCountry ||
        syncedCurrency !== normalizedCurrency
      ) {
        applySelection({
          country: syncedCountry,
          currency: syncedCurrency,
          manualSelection,
        })
      }

      return { country: syncedCountry, currency: syncedCurrency }
    },
    [applySelection, syncServerSelection]
  )

  useEffect(() => {
    const persistedCountry = readClientPersistedCountry()
    const manualSelection = readClientCountryManualSelection()
    const inferredManualSelection = persistedCountry ? manualSelection : false
    const resolvedCountry = resolveClientCountrySelection()
    const resolvedCurrency = toNormalizedCurrency(
      resolveClientCurrencySelection(resolvedCountry),
      resolvedCountry
    )

    setSelectedCountry(resolvedCountry)
    setSelectedCurrency(resolvedCurrency)

    const cookieCountry = normalizeSupportedCountryCode(
      readCookieValue(COUNTRY_COOKIE_KEY)
    )
    const cookieCurrency = normalizeCurrencyCode(
      readCookieValue(CURRENCY_COOKIE_KEY)
    )
    const cookieManualSelection = parseManualSelectionFlag(
      readCookieValue(COUNTRY_MANUAL_COOKIE_KEY)
    )

    const hasCountryCookie = cookieCountry !== null
    const hasCurrencyCookie = cookieCurrency !== null
    const hasManualCookie = cookieManualSelection !== null
    const hasMissingCookies =
      !hasCountryCookie || !hasCurrencyCookie || !hasManualCookie
    const hasMismatch =
      (hasCountryCookie && cookieCountry !== resolvedCountry) ||
      (hasCurrencyCookie &&
        normalizeCurrency(cookieCurrency, STORE_BASE_CURRENCY) !==
          resolvedCurrency) ||
      (hasManualCookie && cookieManualSelection !== inferredManualSelection)
    const shouldSyncServer = hasMissingCookies || hasMismatch

    if (shouldSyncServer) {
      persistClientSideSelection({
        country: resolvedCountry,
        currency: resolvedCurrency,
        manualSelection: inferredManualSelection,
      })
    }

    if (shouldSyncServer) {
      const syncKey = `${resolvedCountry}:${resolvedCurrency}:${
        inferredManualSelection ? "1" : "0"
      }`
      if (!syncedInitialSelectionKeys.has(syncKey)) {
        syncedInitialSelectionKeys.add(syncKey)
        void syncServerSelection({
          countryCode: resolvedCountry,
          currency: resolvedCurrency,
          manualSelection: inferredManualSelection,
        }).catch(() => {
          syncedInitialSelectionKeys.delete(syncKey)
          return null
        })
      }
    }

    emitCountrySelectionEvent({
      country: resolvedCountry,
      currency: resolvedCurrency,
    })
    emitCurrencySelectionEvent(resolvedCurrency)
  }, [persistClientSideSelection, syncServerSelection])

  useEffect(() => {
    if (!open) return
    setPendingCountry(selectedCountry)
    setPendingCurrency(selectedCurrency)
    setCountryQuery("")
    setCountryListOpen(false)
  }, [open, selectedCountry, selectedCurrency])

  useEffect(() => {
    if (!open || !countryListOpen) return
    const handleOutsideClick = (event: MouseEvent) => {
      if (!countryListRef.current) return
      if (countryListRef.current.contains(event.target as Node)) return
      setCountryListOpen(false)
    }
    document.addEventListener("mousedown", handleOutsideClick)
    return () => document.removeEventListener("mousedown", handleOutsideClick)
  }, [countryListOpen, open])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      setOpen(false)
      setCountryListOpen(false)
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [open])

  useEffect(() => {
    const countryHandler = (event: Event) => {
      const detail = (
        event as CustomEvent<{ country?: string; currency?: string }>
      ).detail
      const nextCountry = normalizeSupportedCountryCode(detail?.country)
      if (!nextCountry) return
      setSelectedCountry(nextCountry)
      if (detail?.currency) {
        setSelectedCurrency(toNormalizedCurrency(detail.currency, nextCountry))
      }
    }

    const currencyHandler = (event: Event) => {
      const detail = (event as CustomEvent<{ currency?: string }>).detail
      if (!detail?.currency) return
      setSelectedCurrency(
        toNormalizedCurrency(detail.currency, selectedCountry)
      )
    }

    if (typeof window !== "undefined") {
      window.addEventListener("lealti:country", countryHandler as EventListener)
      window.addEventListener(
        "lealti:currency",
        currencyHandler as EventListener
      )
      return () => {
        window.removeEventListener(
          "lealti:country",
          countryHandler as EventListener
        )
        window.removeEventListener(
          "lealti:currency",
          currencyHandler as EventListener
        )
      }
    }

    return undefined
  }, [selectedCountry])

  const handleSelectCountry = useCallback((countryCode: string) => {
    const normalizedCountry =
      normalizeSelectableCountryCode(countryCode) || DEFAULT_COUNTRY_CODE
    const mappedCurrency = toNormalizedCurrency(undefined, normalizedCountry)
    setPendingCountry(normalizedCountry)
    setPendingCurrency(mappedCurrency)
    setCountryQuery("")
    setCountryListOpen(false)
  }, [])

  const handleAccept = useCallback(async () => {
    const normalizedCountry =
      normalizeSelectableCountryCode(pendingCountry) || DEFAULT_COUNTRY_CODE
    const normalizedCurrency = toNormalizedCurrency(
      pendingCurrency,
      normalizedCountry
    )

    await persistSelection({
      country: normalizedCountry,
      currency: normalizedCurrency,
      manualSelection: true,
    })
    setOpen(false)
    setCountryListOpen(false)
  }, [pendingCountry, pendingCurrency, persistSelection])

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-3 text-sm font-lealti-bold tracking-[0.08em] text-black uppercase transition hover:opacity-70"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Cambiar país y moneda"
      >
        <span>{selectedCountryOption.name.toUpperCase()}</span>
        <span className="text-lg leading-none" aria-hidden="true">
          →
        </span>
      </button>

      {open ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 sm:p-8">
          <button
            type="button"
            className="absolute inset-0 bg-black/30"
            aria-label="Cerrar selector de país y moneda"
            onClick={() => {
              setOpen(false)
              setCountryListOpen(false)
            }}
          />

          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="footer-locale-title"
            className="relative z-[1] w-full max-w-[580px] bg-white px-6 py-8 shadow-[0_18px_72px_rgba(0,0,0,0.22)] sm:px-10 sm:py-10"
          >
            <div className="flex items-start justify-between gap-4">
              <h2
                id="footer-locale-title"
                className="text-xl font-lealti-bold tracking-wide text-[#1b1b1b] uppercase sm:text-2xl sm:leading-[1.2]"
              >
                ¿En qué país te gustaría comprar?
              </h2>
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  setCountryListOpen(false)
                }}
                className="text-[#1b1b1b] transition hover:opacity-70"
                aria-label="Cerrar"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8"
                  aria-hidden="true"
                >
                  <path
                    d="M4 4L20 20M20 4L4 20"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="square"
                  />
                </svg>
              </button>
            </div>

            <div className="mt-6">
              <p className="text-sm font-lealti-light text-[#1f1f1f]">
                Selecciona un país o región
              </p>

              <div
                ref={countryListRef}
                className="mt-4 border border-[#1b1b1b]"
              >
                {!countryListOpen ? (
                  <button
                    type="button"
                    onClick={() => setCountryListOpen(true)}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left sm:px-5"
                    aria-label="Abrir lista de países"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 shrink-0 text-[#1f1f1f]"
                      aria-hidden="true"
                    >
                      <path
                        d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18M16 10a4 4 0 01-8 0"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className="flex-1 text-base font-lealti-light text-[#1f1f1f]">
                      {pendingCountryOption.name}
                    </span>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 shrink-0 text-[#1f1f1f]"
                      aria-hidden="true"
                    >
                      <path
                        d="M6 10L12 16L18 10"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="square"
                      />
                    </svg>
                  </button>
                ) : (
                  <div className="flex items-center gap-3 px-4 py-3 sm:px-5">
                    <input
                      type="text"
                      value={countryQuery}
                      autoFocus
                      onChange={(event) => {
                        setCountryQuery(event.target.value)
                      }}
                      className="w-full bg-transparent text-base font-lealti-light text-[#1f1f1f] outline-none placeholder:text-[#6a6a6a]"
                      placeholder="Buscar país…"
                      aria-label="Buscar país"
                    />
                    <button
                      type="button"
                      onClick={() => setCountryListOpen(false)}
                      className="inline-flex h-8 w-8 items-center justify-center text-[#1f1f1f]"
                      aria-label="Cerrar lista de países"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        aria-hidden="true"
                      >
                        <path
                          d="M6 14L12 8L18 14"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="square"
                        />
                      </svg>
                    </button>
                  </div>
                )}

                {countryListOpen ? (
                  <ul className="max-h-64 overflow-y-auto border-t border-[#d2d2d2]">
                    {filteredCountries.length ? (
                      filteredCountries.map((option) => {
                        const isSelected = option.code === pendingCountry
                        return (
                          <li key={option.code}>
                            <button
                              type="button"
                              onClick={() => handleSelectCountry(option.code)}
                              className={`flex w-full items-center gap-3 px-4 py-3 text-left font-lealti-light transition ${
                                isSelected
                                  ? "bg-[#ebebeb] text-[#1b1b1b]"
                                  : "bg-white text-[#1f1f1f] hover:bg-[#f5f5f5]"
                              }`}
                            >
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 shrink-0"
                                aria-hidden="true"
                              >
                                <path
                                  d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18M16 10a4 4 0 01-8 0"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <span className="text-base leading-6">
                                {option.name}
                              </span>
                            </button>
                          </li>
                        )
                      })
                    ) : (
                      <li className="px-5 py-4 text-base text-[#4f4f4f]">
                        No se encontraron países.
                      </li>
                    )}
                  </ul>
                ) : null}
              </div>
            </div>

            <div className="mt-5">
              <p className="text-sm font-lealti-light text-[#1f1f1f]">
                Selecciona la moneda
              </p>
              <div className="mt-3 border border-[#1b1b1b]">
                <select
                  value={pendingCurrency}
                  onChange={(event) => {
                    setPendingCurrency(
                      toNormalizedCurrency(event.target.value, pendingCountry)
                    )
                  }}
                  className="w-full appearance-none bg-white px-4 py-3 text-base font-lealti-light text-[#1f1f1f] outline-none"
                  aria-label="Seleccionar moneda"
                >
                  {currencies.map((currencyOption) => (
                    <option key={currencyOption} value={currencyOption}>
                      {currencyOption}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button
              type="button"
              onClick={() => void handleAccept()}
              className="mt-5 w-full bg-black px-4 py-4 text-center text-sm font-lealti-bold tracking-[0.08em] text-white uppercase transition hover:opacity-90"
            >
              ACEPTAR
            </button>

            <p className="mt-5 text-sm font-lealti-light leading-6 text-[#242424]">
              Al continuar, aceptas los{" "}
              <span className="font-lealti-bold">
                Términos &amp; Condiciones
              </span>{" "}
              de tu país o región elegido.
            </p>
          </div>
        </div>
      ) : null}
    </>
  )
}
